// ── gameScoreModel — the score header's status + color derivation ────
//
// Extracted from GameScoreHeader so the compact sibling (#878) reads the
// SAME rules rather than a second copy of them. Nothing here is new: the
// branch order, the scoreless-final guard and the 3-way rank coloring are
// lifted verbatim from the header that shipped them.
//
// Deliberately clock-free. `scheduledStartMs` and the countdown belong to
// whichever row reads `useNow()`, so a model call can be asserted without
// pinning a clock — and two headers rendering the same game agree on
// status and color even when they disagree on how to show the time.

import { formatGameStatus, isConfirmedFinal } from '@/lib/sport/periodLabel';
import { colorForSide, deriveWinnerSide, rankColor } from './teamColors';
import type {
  OddsStreamConsensus,
  OddsStreamGame,
  OddsStreamGameState,
} from '../../services/oddsStream';

export interface GameScoreInput {
  /** From snapshot.game when the snapshot has arrived. Null during the
   *  connect window — falls back to fallbackTeams. */
  game: OddsStreamGame | null;
  /** Latest gameState event (or snapshot.gameState on first paint). */
  gameState: OddsStreamGameState | null;
  /** Latest Full-Game consensus — drives the winner-based team color. */
  latestConsensus: OddsStreamConsensus | null;
  /** Fallback team codes from the URL — used before the snapshot arrives. */
  fallbackTeams: readonly [string, string];
}

export interface GameScoreModel {
  awayCode: string;
  homeCode: string;
  /** Null when the venue has not sent a numeric score, INCLUDING while the
   *  game reads live — see `isLive` for why that combination is real. */
  awayScore: number | null;
  homeScore: number | null;
  /** statusId 2. True independently of whether a score arrived with it. */
  isLive: boolean;
  /** statusId 3 AND both scores numeric (`isConfirmedFinal`). */
  isFinal: boolean;
  /** Neither confirmed-final nor live — this is where a malformed scoreless
   *  "final" lands, which is why it reads as pre rather than as final. */
  isPre: boolean;
  /** A real score exists to render. Live-without-score is the gap this
   *  distinguishes: `isLive && !showScore`. */
  showScore: boolean;
  periodLabel: string | null;
  awayColor: string;
  homeColor: string;
}

export function deriveGameScoreModel({
  game,
  gameState,
  latestConsensus,
  fallbackTeams,
}: GameScoreInput): GameScoreModel {
  const awayCode = game?.awayTeam ?? fallbackTeams[0];
  const homeCode = game?.homeTeam ?? fallbackTeams[1];
  const statusId = gameState?.statusId ?? null;
  const awayScore = typeof gameState?.awayScore === 'number' ? gameState.awayScore : null;
  const homeScore = typeof gameState?.homeScore === 'number' ? gameState.homeScore : null;
  const isLive = statusId === 2;
  // FINAL requires the score (isConfirmedFinal) — a statusId 3 with null
  // scores is a stale/garbage record (soccer gameStates, 2026-06-12), and a
  // scoreless FINAL banner on a live game is worse than the pre-game
  // fallback.
  const isFinal = isConfirmedFinal(gameState);
  const isPre = !isFinal && !isLive;
  const periodLabel = formatGameStatus(gameState, game?.sport
    ?? (typeof gameState?.sport === 'string' ? gameState.sport : null));
  const showScore = (isLive || isFinal) && awayScore !== null && homeScore !== null;

  // Mirror the chart's color logic — green for whoever's currently
  // leading the consensus (or whoever won the final). The two surfaces
  // import the same helpers so any flip is in lockstep. Soccer 3-way
  // (drawFairProbCents present) uses rank colors across all three
  // outcomes — a team can render grey when the Draw outranks it.
  const drawCents = latestConsensus?.drawFairProbCents ?? null;
  const rankAll =
    latestConsensus != null && drawCents != null
      ? [latestConsensus.awayFairProbCents, latestConsensus.homeFairProbCents, drawCents]
      : null;
  const winnerSide = deriveWinnerSide(latestConsensus, gameState);
  const awayColor =
    rankAll && latestConsensus
      ? rankColor(latestConsensus.awayFairProbCents, rankAll)
      : colorForSide('away', winnerSide);
  const homeColor =
    rankAll && latestConsensus
      ? rankColor(latestConsensus.homeFairProbCents, rankAll)
      : colorForSide('home', winnerSide);

  return {
    awayCode,
    homeCode,
    awayScore,
    homeScore,
    isLive,
    isFinal,
    isPre,
    showScore,
    periodLabel,
    awayColor,
    homeColor,
  };
}

/** Time until start, in the header's inline form (no parentheses — both
 *  headers supply their own container chrome). Same buckets GameCountdown
 *  uses: whole days past 24h, `2h 14m` past the hour, else `mm:ss`. */
export function formatCountdown(diffMs: number): string {
  const totalSec = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSec / 86400);
  if (days >= 1) return `${days}d`;
  const hours = Math.floor(totalSec / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  if (hours >= 1) return `${hours}h ${mins}m`;
  const secs = totalSec % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
